import { Helmet } from 'react-helmet';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  noindex?: boolean;
  jsonLd?: object;
  locale?: string;
}

const BASE_URL = 'https://sentinelcrop.com';
const DEFAULT_IMAGE = `${BASE_URL}/lovable-uploads/2c9a0df3-5560-4f75-b22a-0722b733c4b9.png`;

const defaultMeta = {
  title: 'Sentinel Crop | Parcel-Level Agronomic Intelligence & Evidence Reports',
  description: 'Turn satellite, weather and field data into parcel-level agronomic decisions, risk signals and defensible reports with SHA-256 Document ID verification.',
  keywords: 'parcel-level agronomic intelligence, satellite crop monitoring, Copernicus Sentinel-2, NDVI, NDWI, FAO-56 irrigation analytics, defensible parcel reports, SHA-256 Document ID, decision-support agriculture, cooperative software, agronomic advisor software',
};

export const SEO = ({
  title,
  description = defaultMeta.description,
  keywords = defaultMeta.keywords,
  image = DEFAULT_IMAGE,
  url,
  type = 'website',
  noindex = false,
  jsonLd,
  locale,
}: SEOProps) => {
  const fullTitle = title 
    ? `${title} | Sentinel Crop`
    : defaultMeta.title;
  
  const canonicalUrl = url ? `${BASE_URL}${url}` : BASE_URL;
  const ogLocale = locale === 'es' ? 'es_ES' : 'en_US';
  const altLocale = locale === 'es' ? 'en_US' : 'es_ES';

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      
      {/* Canonical */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Hreflang for EN/ES */}
      <link rel="alternate" hrefLang="en" href={canonicalUrl} />
      <link rel="alternate" hrefLang="es" href={canonicalUrl} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Sentinel Crop — satellite intelligence for cooperatives, insurers and large producers" />
      <meta property="og:site_name" content="Sentinel Crop" />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:locale:alternate" content={altLocale} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content="Sentinel Crop — satellite intelligence for cooperatives, insurers and large producers" />
      <meta name="twitter:site" content="@sentinelcrop" />
      
      {/* LinkedIn */}
      <meta property="linkedin:title" content={fullTitle} />
      <meta property="linkedin:description" content={description} />
      
      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

// Pre-defined SEO configurations for main pages
export const SEO_CONFIG = {
  home: {
    title: undefined, // Uses default
    description: 'Turn satellite, weather and field data into parcel-level agronomic decisions, risk signals and defensible reports with SHA-256 Document ID verification.',
    keywords: 'parcel-level agronomic intelligence, satellite crop monitoring, Copernicus Sentinel-2, NDVI, NDWI, FAO-56 irrigation analytics, defensible parcel reports, SHA-256 Document ID, decision-support agriculture',
  },
  features: {
    title: 'Platform Features',
    description: 'Sentinel-2 monitoring, FAO-56 irrigation analytics and audit-support evidence — built for cooperatives, insurers and large producers.',
    keywords: 'satellite monitoring, NDVI analysis, FAO-56, claim review support, irrigation analytics, frost detection, drought monitoring, agri-insurance evidence, cooperative dashboard',
  },
  pricing: {
    title: 'Pricing — Free, Pro & Enterprise',
    description: 'Free for individual producers. Pro for professional farms. Enterprise for cooperatives and insurers with API access, white-label and volume pricing above 5,000 ha.',
    keywords: 'agricultural software pricing, satellite monitoring cost, cooperative software, insurance platform pricing, enterprise agri-tech, sentinel crop plans',
  },
  insurance: {
    title: 'Parametric Insurance Modules (Early Access)',
    description: 'Early-access parametric modules for design partners: trigger logic and payout simulation for frost, drought, hail and excess rain. Not a licensed policy.',
    keywords: 'early-access parametric insurance modules, weather triggers, frost risk, drought monitoring, hail risk, claim review support, crop evidence',
  },
  technology: {
    title: 'Our Technology',
    description: 'Built on Copernicus Sentinel-1/2 imagery, FAO-56 agronomic methodology and an audit-support evidence trail. Transparent metrics, field validation in progress.',
    keywords: 'Copernicus satellites, Sentinel-2, Sentinel-1 SAR, FAO-56, satellite imagery, remote sensing agriculture, audit-support evidence',
  },
  claims: {
    title: 'Claim Review Support with Satellite Evidence',
    description: 'Support loss-adjustment workflows with timestamped Sentinel-2 imagery, weather context and a tamper-evident evidence trail per parcel. Assists adjusters, not replaces them.',
    keywords: 'claim review support, satellite evidence, loss adjustment, damage detection support, audit-support evidence, agri-insurance',
  },
  riskAssessment: {
    title: 'Risk Assessment',
    description: 'Comprehensive risk analysis powered by historical data, weather patterns, and AI. Understand your farm\'s exposure to frost, drought, hail, and other perils.',
    keywords: 'risk assessment, farm risk analysis, weather risk, climate risk, agricultural risk management, crop vulnerability',
  },
  contact: {
    title: 'Contact Us',
    description: 'Get in touch with Sentinel Crop. Schedule a demo, request a quote, or learn about our early-access parametric insurance modules.',
    keywords: 'contact, demo, quote, agricultural insurance support, farm insurance inquiry',
  },
  about: {
    title: 'About Us',
    description: 'Sentinel Crop builds satellite intelligence tools for cooperatives, insurers and large producers — replacing opinion with transparent data in every agronomic decision.',
    keywords: 'about sentinel crop, agricultural technology company, agritech, satellite agriculture, audit-support evidence',
  },
  login: {
    title: 'Sign In',
    description: 'Access your Sentinel Crop dashboard. Monitor your fields, view alerts, and manage your insurance policies.',
    noindex: true,
  },
  register: {
    title: 'Create Account',
    description: 'Join Sentinel Crop and start monitoring your parcels with satellite-grade evidence. Free tier available, no credit card required.',
    keywords: 'sign up, create account, farm insurance registration, free trial',
  },
  dashboard: {
    title: 'Dashboard',
    noindex: true,
  },
  help: {
    title: 'Help Center',
    description: 'Find answers to common questions about Sentinel Crop. Learn how to set up monitoring, file claims, and maximize your coverage.',
    keywords: 'help center, FAQ, support, how to, guide, tutorial, customer support',
  },
  privacy: {
    title: 'Privacy Policy',
    description: 'Learn how Sentinel Crop protects your data. Our privacy policy explains data collection, usage, and your rights under GDPR.',
    noindex: true,
  },
  terms: {
    title: 'Terms of Service',
    description: 'Terms and conditions for using Sentinel Crop\'s parametric insurance platform and services.',
    noindex: true,
  },
};

export default SEO;
